import * as XLSX from "xlsx";
import {
  CAPTURE_METHOD_OPTIONS,
  SAMPLING_METHOD_OPTIONS,
  normalizeCaptureMethod,
  normalizeSamplingMethod,
  isAnglingSamplingMethod,
  requiresEffortUnits,
  effortTypeForSamplingMethod,
  buildFishSamplePayload,
} from "./samplingCpueUtils";
import { STOCKING_SPECIES_ABBR_FALLBACK } from "./samplingDisplayLabels";

/** PondStat multi-date upload template columns (ReelBiology-compatible). */
export const POND_UPLOAD_COLUMNS = [
  { key: "Date", header: "Date", required: true },
  { key: "Species", header: "Species", required: true },
  { key: "Length", header: "Length", required: true },
  { key: "Weight", header: "Weight", required: true },
  { key: "TagNumber", header: "Tag Number", required: false },
  { key: "Harvested", header: "Harvested", required: false },
];

/** Extra ReelBiology-style abbreviations → full names used when DB abbr differs. */
export const REELBIOLOGY_SPECIES_ALIASES = {
  lmb: "largemouth bass",
  blg: "bluegill",
  bg: "bluegill",
  re: "redear sunfish",
  rsf: "redear sunfish",
  smb: "smallmouth bass",
  "green sf": "green sunfish",
  gsf: "green sunfish",
  "hybrid sf": "hybrid sunfish",
  "other sf": "sunfish",
  "golden s": "golden shiner",
  gsh: "golden shiner",
  "g shad": "gizzard shad",
  gsd: "gizzard shad",
  "t shad": "threadfin shad",
  tfs: "threadfin shad",
  "crappie w": "white crappie",
  wcp: "white crappie",
  "crappie b": "black crappie",
  bcp: "black crappie",
  "c catfish": "channel catfish",
  ccf: "channel catfish",
  "b catfish": "blue catfish",
  "y catfish": "yellow catfish",
  bullhead: "bullhead",
  "common carp": "common carp",
  "hyb strip": "hybrid striped bass",
  hsb: "hybrid striped bass",
  "yellow perch": "yellow perch",
  "rainbow t": "rainbow trout",
  rbt: "rainbow trout",
};

function normalizeKey(key) {
  return String(key || "")
    .replace(/\s+/g, "")
    .replace(/[_-]/g, "")
    .toLowerCase();
}

function cellStr(value) {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const m = value.getMonth() + 1;
    const d = value.getDate();
    const y = value.getFullYear();
    return `${m}/${d}/${y}`;
  }
  return String(value).trim();
}

/**
 * Parse Excel serial date or common date strings → { iso: YYYY-MM-DD, label, ms } | null
 */
export function parseUploadDate(value) {
  if (value == null || value === "") return null;

  if (typeof value === "number" && Number.isFinite(value)) {
    // Excel serial (days since 1899-12-30)
    const epoch = Date.UTC(1899, 11, 30);
    const ms = epoch + Math.round(value) * 86400000;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return dateParts(d);
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dateParts(value);
  }

  const s = cellStr(value);
  if (!s) return null;

  // M/D/YYYY or MM/DD/YYYY
  const mdy = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (mdy) {
    let y = parseInt(mdy[3], 10);
    if (y < 100) y += 2000;
    const m = parseInt(mdy[1], 10);
    const d = parseInt(mdy[2], 10);
    const dt = new Date(y, m - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
    return dateParts(dt);
  }

  // YYYY-MM-DD
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const y = parseInt(iso[1], 10);
    const m = parseInt(iso[2], 10);
    const d = parseInt(iso[3], 10);
    const dt = new Date(y, m - 1, d);
    if (Number.isNaN(dt.getTime())) return null;
    return dateParts(dt);
  }

  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  return dateParts(parsed);
}

function dateParts(d) {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const iso = `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const label = `${m}/${day}/${y}`;
  const displayName = `${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}-${y}`;
  return { iso, label, displayName, ms: d.getTime() };
}

export function mapHarvestedToStatus(value) {
  if (value == null || value === "") return "Released";
  if (typeof value === "boolean") return value ? "Culled" : "Released";
  if (typeof value === "number") return value ? "Culled" : "Released";
  const s = String(value).trim().toLowerCase();
  if (["1", "true", "yes", "y", "x", "checked", "culled", "harvested"].includes(s)) {
    return "Culled";
  }
  if (["0", "false", "no", "n", "released", "unchecked"].includes(s)) {
    return "Released";
  }
  // Excel checkbox sometimes stores TRUE/FALSE
  if (s === "true") return "Culled";
  return "Released";
}

export function mapStatusColumn(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim().toLowerCase();
  if (s === "culled") return "Culled";
  if (s === "released") return "Released";
  return null;
}

function getRowField(row, ...aliases) {
  if (!row || typeof row !== "object") return undefined;
  const map = {};
  Object.keys(row).forEach((k) => {
    map[normalizeKey(k)] = row[k];
  });
  for (const alias of aliases) {
    const v = map[normalizeKey(alias)];
    if (v !== undefined) return v;
  }
  return undefined;
}

/**
 * Build lookup from fish type list (API FishTypes or pond fishes).
 * @param {Array<{id?:number, FishTypeID?:number, Name?:string, FishTypeName?:string, Abbreviation?:string, FishTypeAbbreviation?:string, WsIP?:string, WsMG?:string, WsIPEquation?:string, WsMGEquation?:string}>} fishTypes
 */
export function buildSpeciesLookup(fishTypes) {
  const byKey = new Map();
  const list = Array.isArray(fishTypes) ? fishTypes : [];

  const add = (key, entry) => {
    const k = String(key || "").trim().toLowerCase();
    if (!k || byKey.has(k)) return;
    byKey.set(k, entry);
  };

  list.forEach((ft) => {
    const id = ft.FishTypeID ?? ft.id;
    if (id == null) return;
    const name = String(ft.FishTypeName ?? ft.Name ?? "").trim();
    const abbr = String(ft.FishTypeAbbreviation ?? ft.Abbreviation ?? "").trim();
    const entry = {
      FishTypeID: Number(id),
      Name: name,
      Abbreviation: abbr,
      WsIPEquation: ft.WsIPEquation ?? ft.WsIP ?? "",
      WsMGEquation: ft.WsMGEquation ?? ft.WsMG ?? "",
    };
    add(name, entry);
    add(abbr, entry);
    // Reverse fallback map: LMB → largemouth bass entry if name matches
    const fallbackAbbr = STOCKING_SPECIES_ABBR_FALLBACK[name.toLowerCase()];
    if (fallbackAbbr) add(fallbackAbbr, entry);
  });

  // Alias table → resolve to name key then entry
  Object.entries(REELBIOLOGY_SPECIES_ALIASES).forEach(([alias, canonicalName]) => {
    const entry = byKey.get(canonicalName.toLowerCase());
    if (entry) add(alias, entry);
  });

  return byKey;
}

export function resolveSpecies(rawSpecies, lookup) {
  const raw = cellStr(rawSpecies);
  if (!raw) return { error: "Species is required" };
  const key = raw.toLowerCase();
  let entry = lookup.get(key);
  if (!entry) {
    const aliasTarget = REELBIOLOGY_SPECIES_ALIASES[key];
    if (aliasTarget) entry = lookup.get(aliasTarget.toLowerCase());
  }
  if (!entry) {
    return { error: `Unknown species: "${raw}"` };
  }
  return { value: entry };
}

/**
 * Relative weight (Wr) is stored as a ratio (1.0 = 100%).
 * Values above this threshold are flagged for user confirmation on Excel import.
 */
export const WR_FLAG_RATIO = 3;

/** True when Wr ratio exceeds the flag threshold (e.g. Wr 3.0 = 300%). */
export function isWrAboveFlagThreshold(wr) {
  const n = Number(wr);
  return Number.isFinite(n) && n > WR_FLAG_RATIO;
}

export function formatWrRatioAsPercentLabel(wr) {
  const n = Number(wr);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

export function calculateWsWr({ length, weight, equation }) {
  const L = parseFloat(length);
  const W = parseFloat(weight);
  if (!equation || !Number.isFinite(L) || L <= 0) {
    return { ws: 0, wr: 0 };
  }
  try {
    if (!/^[0-9+\-*/(). L^log]*$/.test(equation)) {
      return { ws: 0, wr: 0 };
    }
    const replaced = equation.replace(/L/g, String(L)).replace(/\^/g, "**");
    // eslint-disable-next-line no-new-func
    const calculatedWs = Function(`return ${replaced}`)();
    if (!Number.isFinite(calculatedWs) || calculatedWs <= 0) {
      return { ws: 0, wr: 0 };
    }
    const ws = parseFloat(calculatedWs.toFixed(3));
    const wr =
      Number.isFinite(W) && W > 0
        ? parseFloat((W / calculatedWs).toFixed(3))
        : 0;
    return { ws, wr };
  } catch {
    return { ws: 0, wr: 0 };
  }
}

/**
 * Normalize raw sheet rows into canonical objects.
 */
export function normalizeUploadRows(rawRows) {
  return (rawRows || [])
    .map((row, index) => {
      const dateRaw = getRowField(row, "Date", "Sampling Date", "Sample Date");
      const species = getRowField(row, "Species", "Fish Type", "FishTypeName", "FishType");
      const length = getRowField(row, "Length", "Len");
      const weight = getRowField(row, "Weight", "Wt");
      const tag = getRowField(row, "Tag Number", "TagNumber", "Tag");
      const harvested = getRowField(row, "Harvested", "Harvest");
      const statusCol = getRowField(row, "Status", "Culled");

      const hasAny =
        cellStr(dateRaw) ||
        cellStr(species) ||
        (length != null && length !== "") ||
        (weight != null && weight !== "");
      if (!hasAny) return null;

      return {
        rowNumber: index + 2, // assuming header row 1
        dateRaw,
        species: cellStr(species),
        length,
        weight,
        tagNumber: cellStr(tag),
        harvested,
        statusCol,
      };
    })
    .filter(Boolean);
}

/**
 * Parse workbook ArrayBuffer / CSV text → raw objects.
 */
export function parseWorkbookFile(fileBuffer, fileName = "") {
  const name = String(fileName || "").toLowerCase();
  if (name.endsWith(".csv")) {
    throw new Error("Please upload an Excel file (.xlsx or .xls)");
  }
  const wb = XLSX.read(fileBuffer, { type: "array", cellDates: true });
  // Prefer a sheet named like "Sampling Data" / first data sheet
  const preferred =
    wb.SheetNames.find((n) => /sampling\s*data/i.test(n)) ||
    wb.SheetNames.find((n) => !/instruction/i.test(n)) ||
    wb.SheetNames[0];
  const sheet = wb.Sheets[preferred];
  if (!sheet) {
    throw new Error("No worksheet found in file");
  }
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: true });
  return { sheetName: preferred, rows };
}

/**
 * Validate + group rows by date for preview and commit payload.
 * @param {object} opts
 * @param {Array} opts.normalizedRows
 * @param {Map} opts.speciesLookup
 * @param {string} opts.lengthUnitName - "mm" | "in" | "inches" etc.
 * @param {Array} opts.existingSessions - [{ id, Date, Name }]
 * @param {string} opts.pondName
 */
export function buildUploadPreview({
  normalizedRows,
  speciesLookup,
  lengthUnitName,
  existingSessions = [],
  pondName = "Pond",
}) {
  const errors = [];
  const warnings = [];
  const byDate = new Map();
  const useMetric = /mm/i.test(String(lengthUnitName || ""));

  const existingByIso = new Map();
  (existingSessions || []).forEach((s) => {
    const parsed = parseUploadDate(s.Date ?? s.date);
    if (parsed) existingByIso.set(parsed.iso, s);
  });

  for (const row of normalizedRows) {
    const dateInfo = parseUploadDate(row.dateRaw);
    if (!dateInfo) {
      errors.push({ rowNumber: row.rowNumber, message: `Invalid date: "${row.dateRaw}"` });
      continue;
    }
    if (!row.species) {
      errors.push({ rowNumber: row.rowNumber, message: "Species is required" });
      continue;
    }
    const speciesRes = resolveSpecies(row.species, speciesLookup);
    if (speciesRes.error) {
      errors.push({ rowNumber: row.rowNumber, message: speciesRes.error });
      continue;
    }
    const length = parseFloat(row.length);
    const weight = parseFloat(row.weight);
    if (!Number.isFinite(length) || length <= 0) {
      errors.push({ rowNumber: row.rowNumber, message: "Length must be a positive number" });
      continue;
    }
    if (!Number.isFinite(weight) || weight <= 0) {
      errors.push({ rowNumber: row.rowNumber, message: "Weight must be a positive number" });
      continue;
    }

    const equation = useMetric
      ? speciesRes.value.WsMGEquation
      : speciesRes.value.WsIPEquation;
    const { ws, wr } = calculateWsWr({ length, weight, equation });

    let status = mapStatusColumn(row.statusCol);
    if (!status) status = mapHarvestedToStatus(row.harvested);

    if (!byDate.has(dateInfo.iso)) {
      const existing = existingByIso.get(dateInfo.iso);
      byDate.set(dateInfo.iso, {
        dateIso: dateInfo.iso,
        dateLabel: dateInfo.label,
        displayNameSuffix: dateInfo.displayName,
        name: existing?.Name || `${pondName}-${dateInfo.displayName}`,
        existingPondDID: existing?.id ?? existing?.PondDID ?? null,
        isAppend: !!(existing?.id ?? existing?.PondDID),
        samples: [],
        speciesIds: new Set(),
        speciesNames: new Set(),
      });
    }
    const bucket = byDate.get(dateInfo.iso);
    bucket.speciesIds.add(speciesRes.value.FishTypeID);
    bucket.speciesNames.add(speciesRes.value.Name || row.species);
    bucket.samples.push({
      rowNumber: row.rowNumber,
      FishTypeID: speciesRes.value.FishTypeID,
      FishTypeName: speciesRes.value.Name || row.species,
      Date: dateInfo.label,
      dateIso: dateInfo.iso,
      Length: Number(length.toFixed(4)),
      Weight: Number(weight.toFixed(4)),
      Ws: Number(Number(ws || 0).toFixed(4)),
      Wr: Number(Number(wr || 0).toFixed(4)),
      TagNumber: row.tagNumber || "",
      Status: status,
    });
  }

  const sessions = [...byDate.values()].sort((a, b) =>
    a.dateIso.localeCompare(b.dateIso)
  );

  if (!normalizedRows.length) {
    errors.push({ rowNumber: 0, message: "No data rows found in the file" });
  }

  return {
    ok: errors.length === 0 && sessions.length > 0,
    errors,
    warnings,
    sessions: sessions.map((s) => ({
      ...s,
      fishTypes: [...s.speciesIds],
      speciesList: [...s.speciesNames],
      rowCount: s.samples.length,
      speciesIds: undefined,
      speciesNames: undefined,
    })),
    totalRows: sessions.reduce((n, s) => n + s.samples.length, 0),
    newSessionCount: sessions.filter((s) => !s.isAppend).length,
    appendSessionCount: sessions.filter((s) => s.isAppend).length,
  };
}

/**
 * Build API body for ImportSamplingByDates.
 */
export function buildImportPayload({
  pondId,
  fisherId,
  settings,
  previewSessions,
}) {
  const samplingMethod =
    normalizeSamplingMethod(settings.samplingMethod) || settings.samplingMethod;
  const defaultCapture =
    normalizeCaptureMethod(settings.defaultCaptureMethod) ||
    (isAnglingSamplingMethod(samplingMethod) ? "Angling" : settings.defaultCaptureMethod);

  return {
    PondID: parseInt(pondId, 10),
    FisherID: fisherId != null ? parseInt(fisherId, 10) : undefined,
    Length_Unit: parseInt(settings.lengthUnitId, 10),
    Weight_Unit: parseInt(settings.weightUnitId, 10),
    SamplingMethod: samplingMethod,
    TimeFishingStarted: settings.timeStarted || "08:00:00",
    TimeFishingStopped: settings.timeStopped || "12:00:00",
    NumberOfAnglers: parseInt(settings.numberOfAnglers, 10) || 1,
    DefaultCaptureMethod: defaultCapture || "",
    DefaultAnglerLabel: settings.defaultAnglerLabel || "Upload",
    sessions: (previewSessions || []).map((s) => ({
      Date: s.dateIso,
      Name: s.name,
      existingPondDID: s.existingPondDID,
      fishTypes: s.fishTypes,
      samples: s.samples.map((sample) =>
        buildFishSamplePayload({
          FishTypeID: sample.FishTypeID,
          Length: sample.Length,
          Weight: sample.Weight,
          Ws: sample.Ws,
          Wr: sample.Wr,
          TagNumber: sample.TagNumber,
          Status: sample.Status,
          CaptureMethod: defaultCapture || undefined,
        })
      ),
    })),
  };
}

/** Max fish rows shown in the upload preview table (matches session import). */
export const POND_UPLOAD_PREVIEW_ROW_LIMIT = 25;

/**
 * Flatten preview sessions → fish rows for the sample preview table.
 */
export function flattenPreviewFishRows(preview, limit = POND_UPLOAD_PREVIEW_ROW_LIMIT) {
  const rows = [];
  for (const session of preview?.sessions || []) {
    for (const sample of session.samples || []) {
      rows.push({
        ...sample,
        Date: sample.Date || session.dateLabel,
        sessionName: session.name,
        isAppend: session.isAppend,
      });
      if (rows.length >= limit) return rows;
    }
  }
  return rows;
}

export function downloadPondUploadTemplate({ fishTypes = [], pondName = "" } = {}) {
  const wb = XLSX.utils.book_new();

  const instructionRows = [
    ["Fish Sampling Import Template (multi-date / pond upload)"],
    [],
    ["Pond", pondName || "—"],
    [],
    ["Required columns on the Sampling Data sheet:"],
    ["Column", "Required?", "Description"],
    ...POND_UPLOAD_COLUMNS.map((col) => [
      col.header,
      col.required ? "Yes" : "No",
      col.key === "Date"
        ? "Sampling date — each distinct date becomes (or appends to) a session"
        : col.key === "Species"
          ? "Full name or abbreviation (copy from Fish Types List)"
          : col.key === "Length"
            ? "Fish length in the units selected on the upload form"
            : col.key === "Weight"
              ? "Fish weight in the units selected on the upload form"
              : col.key === "TagNumber"
                ? "Optional tag / PIT number"
                : "TRUE/Yes/1 = Culled; blank/FALSE/No = Released",
    ]),
    [],
    ["How to use:"],
    ["1. Open the Sampling Data sheet."],
    ["2. Row 1 = column names. Rows below = one fish per row."],
    ["3. Enter Species from the Fish Types List (name or abbreviation)."],
    ["4. Save as .xlsx and upload on the pond Upload Sampling Data page."],
    [],
    ["Notes:"],
    ["Standard W and Relative W columns (if present) are ignored — PondStat recalculates Ws/Wr."],
    ["Optional Status column (Culled/Released) overrides Harvested when both are present."],
  ];
  const wsInst = XLSX.utils.aoa_to_sheet(instructionRows);
  wsInst["!cols"] = [{ wch: 18 }, { wch: 12 }, { wch: 70 }];
  XLSX.utils.book_append_sheet(wb, wsInst, "Instructions");

  const headers = POND_UPLOAD_COLUMNS.map((c) => c.header);
  const example = [
    headers,
    ["8/5/2025", "LMB", 16, 1.56, "", "FALSE"],
    ["8/5/2025", "Bluegill", 7.5, 0.35, "TAG-1", "TRUE"],
    ["8/13/2025", "Largemouth Bass", 18, 2.9, "", ""],
  ];
  const wsData = XLSX.utils.aoa_to_sheet(example);
  wsData["!cols"] = POND_UPLOAD_COLUMNS.map((col) => ({
    wch: col.key === "Species" ? 22 : col.required ? 14 : 12,
  }));
  XLSX.utils.book_append_sheet(wb, wsData, "Sampling Data");

  const fishRows = [
    ["Available Fish Types (copy into Species):"],
    [],
    ["Name", "Abbreviation"],
  ];
  fishTypes.forEach((ft) => {
    fishRows.push([
      ft.Name || ft.FishTypeName || "",
      ft.Abbreviation || ft.FishTypeAbbreviation || "",
    ]);
  });
  if (fishRows.length === 3) {
    Object.entries(STOCKING_SPECIES_ABBR_FALLBACK).forEach(([name, abbr]) => {
      fishRows.push([name.replace(/\b\w/g, (c) => c.toUpperCase()), abbr]);
    });
  }
  const wsFish = XLSX.utils.aoa_to_sheet(fishRows);
  wsFish["!cols"] = [{ wch: 40 }, { wch: 16 }];
  XLSX.utils.book_append_sheet(wb, wsFish, "Fish Types List");

  const statusData = [
    ["Status / Harvested options:"],
    [],
    ["Harvested", "Resulting Status"],
    ["TRUE / Yes / 1", "Culled"],
    ["FALSE / No / blank", "Released"],
    [],
    ["Or use Status column values:"],
    ["Culled"],
    ["Released"],
  ];
  const wsStatus = XLSX.utils.aoa_to_sheet(statusData);
  wsStatus["!cols"] = [{ wch: 28 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, wsStatus, "Status Options");

  const slug = String(pondName || "pond")
    .replace(/\s+/g, "_")
    .replace(/[^\w-]/g, "")
    .toLowerCase()
    .slice(0, 40);
  XLSX.writeFile(wb, `pond_sampling_upload_${slug || "template"}.xlsx`);
}

export {
  SAMPLING_METHOD_OPTIONS,
  CAPTURE_METHOD_OPTIONS,
  isAnglingSamplingMethod,
  requiresEffortUnits,
  effortTypeForSamplingMethod,
  normalizeSamplingMethod,
  normalizeCaptureMethod,
};
